/**
 * +patrols: how the bot tank deals with roaming mobs (docs/design/mob-movement.md §8).
 *
 * Before pulling the next route pack the tank looks at every roaming mob (waypoint patrols and
 * wanderers) that is not part of that pack:
 *   - a patrol near the pull site would join through its own aggro or call for help, so the tank
 *     waits for it to walk off (up to roamWaitMaxMs);
 *   - a roamer that comes close to the group while it is clear of every standing pack is pulled
 *     deliberately, so it is fought on its own instead of in the middle of the next pull;
 *   - a wanderer that never leaves the pull site is pulled first when it can be reached clear of
 *     the pack, else it is accepted as part of the pull.
 * Loose packs are pulled clump by clump (the tank walks at the nearest member, so the far clump
 * stays put); on low keys the tank then drags the fight into the rest of the pack to save time.
 */
import type { PackDef, UnitId, Vec2 } from '@keystone/sim';
import { ASSIST_RADIUS, NPC_WALK_FRACTION, nextPatrolIndex } from '@keystone/sim';
import { dist } from './geometry';
import type { BotMemory } from './memory';
import type { Perception, RUnit } from './perception';

/** At or above this key level the tank pulls loose packs clump by clump; below it drags in the rest. */
export const SPLIT_KEY = 7;
/** Longest the tank waits for a patrol to clear the pull site before pulling anyway (longer on high keys). */
export const roamWaitMaxMs = (level: number): number => (level >= SPLIT_KEY ? 60_000 : 40_000);
/** Extra yards of safety on top of aggro + assist radii. */
const MARGIN = 6;
/** A roamer counts as clear of a pack this far beyond the assist radius. */
const ISO_MARGIN = 4;
/** The tank walks out this far to meet a patrol that is clear of the packs. */
const PATROL_REACH = 40;

export interface Roamer {
  unit: RUnit;
  pack: PackDef;
  patrol: boolean;
}

const idle = (u: RUnit): boolean => u.alive && (u.ai?.mode === 'idle' || u.ai?.mode === 'patrol');

/** Alive, out-of-combat roaming mobs (patrols and wanderers), excluding `exceptPack`. */
export function roamers(p: Perception, exceptPack: string | null): Roamer[] {
  const d = p.dungeon;
  if (!d) return [];
  const out: Roamer[] = [];
  for (const pack of d.packs) {
    if (pack.id === exceptPack || pack.encounterId) continue;
    const st = p.world.packs[pack.id];
    if (!st) continue;
    st.memberIds.forEach((id, i) => {
      const u = p.world.units[id];
      if (!u || !idle(u)) return;
      const patrol = !!pack.path;
      // Wanderers inside a standing pack go with their pack; only lone wanderers count as roamers.
      if (patrol || (pack.spawns[i]?.movement?.type === 'wander' && pack.spawns.every((sp) => sp.movement?.type === 'wander'))) out.push({ unit: u, pack, patrol });
    });
  }
  return out;
}

/** Idle mobs that do not roam (standing packs), excluding the given packs. */
function standing(p: Perception, except: ReadonlySet<string>): RUnit[] {
  const d = p.dungeon;
  if (!d) return [];
  const out: RUnit[] = [];
  for (const pack of d.packs) {
    if (except.has(pack.id) || pack.encounterId || pack.path) continue;
    const st = p.world.packs[pack.id];
    st?.memberIds.forEach((id) => {
      const u = p.world.units[id];
      if (u && idle(u) && !pack.spawns.every((sp) => sp.movement?.type === 'wander')) out.push(u);
    });
  }
  return out;
}

/** Live centre of a pack: patrols where they walk now, standing packs at their alive members' homes. */
export function liveCenter(p: Perception, pack: PackDef): Vec2 | null {
  const ids = p.world.packs[pack.id]?.memberIds ?? [];
  const pts = ids.map((id) => p.world.units[id]).filter((u): u is RUnit => !!u?.alive).map((u) => (pack.path ? u.pos : u.home));
  if (pts.length === 0) return null;
  return { x: pts.reduce((s, q) => s + q.x, 0) / pts.length, y: pts.reduce((s, q) => s + q.y, 0) / pts.length };
}

/** Expected length of a trash fight (ms) at a key level, for patrol timing. */
export const fightEstimateMs = (level: number): number => 30_000 + 1_500 * Math.max(0, level - 2);

/**
 * Where a patrol leader will walk over the next `ms` (samples every ~2 yd, straight lines between
 * points, pauses included as repeated samples). Uses the leader's live waypoint index/direction.
 */
export function predictPatrol(leader: RUnit, pack: PackDef, ms: number): Vec2[] {
  const path = pack.path;
  if (!path || path.points.length === 0) return [leader.pos];
  const speed = Math.max(0.5, leader.moveSpeed * (path.run ? 1 : NPC_WALK_FRACTION));
  let budget = (speed * ms) / 1000;
  let at: Vec2 = leader.pos;
  let i = Math.min(leader.ai?.patrolIndex ?? 0, path.points.length - 1);
  let dir: 1 | -1 = leader.ai?.roam?.dir ?? 1;
  const out: Vec2[] = [at];
  for (let guard = 0; guard < 200 && budget > 0; guard++) {
    const pt = path.points[i]!;
    const d = dist(at, pt.pos);
    const step = Math.min(d, budget);
    const n = Math.max(1, Math.ceil(step / 2));
    for (let k = 1; k <= n; k++) out.push({ x: at.x + ((pt.pos.x - at.x) * step * k) / (n * Math.max(d, 1e-6)), y: at.y + ((pt.pos.y - at.y) * step * k) / (n * Math.max(d, 1e-6)) });
    budget -= step;
    if (budget <= 0) break;
    at = pt.pos;
    budget -= (speed * (pt.waitMs ?? 0)) / 1000;
    const nx = nextPatrolIndex(path, i, dir);
    i = nx.index;
    dir = nx.dir;
  }
  return out;
}

/** Distance from p to segment ab. */
function segDist(q: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const L = dx * dx + dy * dy;
  const t = L > 0 ? Math.max(0, Math.min(1, ((q.x - a.x) * dx + (q.y - a.y) * dy) / L)) : 0;
  return Math.hypot(q.x - (a.x + t * dx), q.y - (a.y + t * dy));
}

export type RoamPlan = { kind: 'pull'; target: RUnit } | { kind: 'wait' } | null;

/**
 * What to do about roamers before pulling `pack` at `site`. Pure function of the perception plus
 * the wait timer in memory (so chooseTarget and planPull agree within one think).
 */
export function roamPlan(p: Perception, mem: BotMemory, pack: PackDef, site: Vec2): RoamPlan {
  const list = roamers(p, pack.id);
  if (list.length === 0) {
    mem.roamWaitSince = null;
    return null;
  }
  const self = p.self;
  const routeMembers = (p.world.packs[pack.id]?.memberIds ?? []).map((id) => p.world.units[id]).filter((u): u is RUnit => !!u?.alive);
  // Groups of roamers by pack (a patrol formation is pulled as one).
  const byPack = new Map<string, Roamer[]>();
  for (const r of list) byPack.set(r.pack.id, [...(byPack.get(r.pack.id) ?? []), r]);
  let wait = false;
  let pull: RUnit | undefined;
  for (const [packId, group] of byPack) {
    const aggro = group[0]!.pack.aggroRadius;
    const assist = group[0]!.pack.assistRadius ?? ASSIST_RADIUS;
    // Near the pull now, or walking through the fight (pack → tank → ranged behind it) before it is likely over.
    const back = { x: self.pos.x + (self.pos.x - site.x) * 0.6, y: self.pos.y + (self.pos.y - site.y) * 0.6 };
    const leaderId = group[0]!.patrol ? (p.world.packs[packId]?.memberIds ?? []).find((id) => p.world.units[id]?.alive) : undefined;
    const leader = leaderId ? p.world.units[leaderId] : undefined;
    const path = leader ? predictPatrol(leader, group[0]!.pack, fightEstimateMs(p.world.mplus?.level ?? 2)) : [];
    const crossesFight = path.some((q) => segDist(q, site, back) <= aggro + 4);
    const nearSite = crossesFight || group.some((r) => dist(r.unit.pos, site) <= aggro + assist + MARGIN);
    const nearUs = group.some((r) => dist(r.unit.pos, self.pos) <= aggro + MARGIN);
    if (!nearSite && !nearUs) continue;
    const others = standing(p, new Set([packId]));
    // Clear of every standing pack (incl. the route pack) now and over the next few yards of its walk?
    // (Helpers are picked from where the roamer stands when it is pulled, so check until the tank gets there.)
    const nearest = group.reduce((b, r) => (dist(r.unit.pos, self.pos) < dist(b.unit.pos, self.pos) ? r : b)).unit;
    const clearOf = (q: Vec2): boolean => others.every((o) => dist(o.pos, q) > assist + ISO_MARGIN) && routeMembers.every((o) => dist(o.pos, q) > assist + ISO_MARGIN);
    const arriveS = dist(nearest.pos, self.pos) / Math.max(1, self.moveSpeed) + 2;
    const isolated = group.every((r) => clearOf(r.unit.pos)) && path.slice(0, 1 + Math.ceil((arriveS * nearest.moveSpeed * NPC_WALK_FRACTION) / 2)).every(clearOf);
    // Patrols are met where they walk (the tank walks out to them); wanderers only when close.
    const reach = dist(nearest.pos, self.pos) <= (group[0]!.patrol ? PATROL_REACH : aggro + 2 * MARGIN);
    // The walk out to it must not brush past a standing pack.
    const walkClear = (): boolean => {
      const n = Math.ceil(dist(self.pos, nearest.pos) / 2);
      for (let k = 1; k < n; k++) {
        const q = { x: self.pos.x + ((nearest.pos.x - self.pos.x) * k) / n, y: self.pos.y + ((nearest.pos.y - self.pos.y) * k) / n };
        if (others.some((o) => dist(o.pos, q) <= aggro + 2) || routeMembers.some((o) => dist(o.pos, q) <= aggro + 2)) return false;
      }
      return true;
    };
    if (isolated && reach && (nearUs || nearSite || !group[0]!.patrol) && walkClear()) {
      if (!pull || dist(nearest.pos, self.pos) < dist(pull.pos, self.pos)) pull = nearest;
    } else if (group[0]!.patrol && nearSite) wait = true;
  }
  if (pull) {
    mem.roamWaitSince = null;
    return { kind: 'pull', target: pull };
  }
  if (!wait) {
    mem.roamWaitSince = null;
    return null;
  }
  mem.roamWaitSince ??= p.now;
  return p.now - mem.roamWaitSince < roamWaitMaxMs(p.world.mplus?.level ?? 2) ? { kind: 'wait' } : null;
}

/** The loose route pack's idle members while part of it is already fighting (low keys drag them in). */
export function restOfPack(p: Perception, pack: PackDef | null): RUnit | undefined {
  if (!pack || pack.link !== 'loose' || (p.world.mplus?.level ?? 2) >= SPLIT_KEY) return undefined;
  const ids = p.world.packs[pack.id]?.memberIds ?? [];
  const members = ids.map((id) => p.world.units[id]).filter((u): u is RUnit => !!u?.alive);
  if (!members.some((u) => !idle(u))) return undefined;
  let best: RUnit | undefined;
  for (const u of members) if (idle(u) && (!best || dist(u.pos, p.self.pos) < dist(best.pos, p.self.pos))) best = u;
  return best;
}

export const unitAlive = (p: Perception, id: UnitId | null): RUnit | undefined => {
  const u = id ? p.world.units[id] : undefined;
  return u && idle(u) ? u : undefined;
};
